/* ------------------------------------------------------------------ */
/*  Emotion types & mappings                                           */
/* ------------------------------------------------------------------ */

export type Emotion =
  | "none"
  | "neutral"
  | "positive"
  | "curious"
  | "surprised"
  | "sad"
  | "angry"
  | "excited"
  | "thoughtful"
  | "playful";

export interface EmotionConfig {
  emoji: string;
  label: string;
  prompt: string;
}

export const EMOTION_MAP: Record<Emotion, EmotionConfig> = {
  none: {
    emoji: "🫥",
    label: "None",
    prompt: "",
  },
  neutral: {
    emoji: "😐",
    label: "Neutral",
    prompt: "Speak in a calm, composed, and matter-of-fact tone.",
  },
  positive: {
    emoji: "😊",
    label: "Positive",
    prompt: "Speak with warmth, enthusiasm, and an upbeat energy.",
  },
  curious: {
    emoji: "🤔",
    label: "Curious",
    prompt: "Speak with genuine curiosity, wonder, and an inquisitive tone.",
  },
  surprised: {
    emoji: "😲",
    label: "Surprised",
    prompt: "Speak with astonishment and wide-eyed amazement.",
  },
  sad: {
    emoji: "😢",
    label: "Sad",
    prompt: "Speak with a soft, melancholic, and empathetic tone.",
  },
  angry: {
    emoji: "😠",
    label: "Angry",
    prompt: "Speak with intensity, frustration, and forceful conviction.",
  },
  excited: {
    emoji: "🤩",
    label: "Excited",
    prompt: "Speak with high energy, enthusiasm, and barely contained excitement.",
  },
  thoughtful: {
    emoji: "🧐",
    label: "Thoughtful",
    prompt: "Speak in a measured, contemplative tone with deliberate pauses.",
  },
  playful: {
    emoji: "😜",
    label: "Playful",
    prompt: "Speak with a lighthearted, teasing, and fun energy.",
  },
};

export const EMOTIONS = Object.keys(EMOTION_MAP) as Emotion[];

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface TranscriptMessage {
  person: string;
  text: string;
  emotion: Emotion;
}

/* ------------------------------------------------------------------ */
/*  JSON transcript schema types                                       */
/* ------------------------------------------------------------------ */

interface DialogueTurn {
  speaker: "Host" | "Guest";
  emotion?: Emotion;
  prompt: string;
  text: string;
}

interface PodcastTranscript {
  dialogue: DialogueTurn[];
}

/* ------------------------------------------------------------------ */
/*  Parsing helpers                                                    */
/* ------------------------------------------------------------------ */

/**
 * Parse a JSON transcript string into an array of { person, text, emotion } messages.
 */
export function parseTranscript(raw: string): TranscriptMessage[] {
    const transcript: PodcastTranscript = JSON.parse(raw);
    if (!transcript.dialogue || !Array.isArray(transcript.dialogue)) {
      return [];
    }

    return transcript.dialogue
      .filter((turn) => turn.text?.trim())
      .map((turn) => {
        let emotion: Emotion = "none";
        // Prefer the structured emotion field if present
        if (turn.emotion && EMOTION_MAP[turn.emotion]) {
          emotion = turn.emotion;
        } else if (turn.prompt) {
          // Legacy fallback: reverse-match prompt string
          const found = EMOTIONS.find(
            (e) => EMOTION_MAP[e].prompt === turn.prompt,
          );
          emotion = found ?? "none";
        }
        return {
          person: turn.speaker,
          text: turn.text.trim(),
          emotion,
        };
      });

}

/**
 * Reformat an array of messages back into the JSON transcript format.
 *
 * Messages are stored 1:1 as dialogue turns (no merging of consecutive
 * same-speaker messages) so the persisted transcript always mirrors the
 * editor: turn indices stay aligned with the message list for the partial
 * re-render (preview/commit) flow. The TTS parser resolves the voice per
 * speaker and handles same-speaker turns in a row; the LLM generation prompt
 * still enforces Host/Guest alternation for generated transcripts.
 */
export function formatTranscript(messages: TranscriptMessage[]): string {
  const transcript: PodcastTranscript = {
    dialogue: messages.map((m) => ({
      speaker: m.person as "Host" | "Guest",
      emotion: m.emotion,
      prompt: m.emotion !== "none" ? (EMOTION_MAP[m.emotion]?.prompt ?? "") : "",
      text: m.text,
    })),
  };

  return JSON.stringify(transcript, null, 2);
}

/**
 * Render a transcript as a plain-text, human-readable script suitable for
 * pasting into a doc, email, or chat. Falls back to the raw string if the
 * transcript can't be parsed at all.
 *
 * Format:
 *   Title: <episode title>
 *
 *   Host: ...
 *   Guest: ...
 *   Host: ...
 *
 * The parser is intentionally permissive: it walks any JSON structure and
 * pulls out the first plausible "person + text" pair it finds in each
 * dialogue turn, so transcripts that don't strictly match the canonical
 * schema still produce a readable script.
 */
export function transcriptToPlainText(raw: string, title?: string | null): string {
  const header = title ? `Title: ${title}\n\n` : "";

  const lines: string[] = [];
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    /* not JSON — fall through to raw fallback below */
  }

  const SPEAKER_KEYS = ["speaker", "person", "role", "name", "host"];
  const TEXT_KEYS = ["text", "content", "message", "utterance", "line"];
  const DIALOGUE_KEYS = ["dialogue", "dialog", "conversation", "turns", "messages", "segments"];

  const pick = (
    obj: Record<string, unknown>,
    keys: string[],
  ): string | undefined => {
    for (const k of keys) {
      const v = obj[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    return undefined;
  };

  const walk = (node: unknown): void => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      for (const child of node) walk(child);
      return;
    }
    const obj = node as Record<string, unknown>;
    const person = pick(obj, SPEAKER_KEYS);
    const text = pick(obj, TEXT_KEYS);
    if (person && text) {
      lines.push(`${person}: ${text}`);
      return;
    }
    for (const key of DIALOGUE_KEYS) {
      if (Array.isArray(obj[key])) {
        walk(obj[key]);
        return;
      }
    }
  };

  if (parsed !== null) {
    walk(parsed);
    if (lines.length > 0) {
      return header + lines.join("\n\n") + "\n";
    }
  }

  return raw;
}
